import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AnalyticsMatter } from '@/hooks/useAnalytics';
import { fullName, type User } from '@/lib/user';

interface AnalyticsFilterUser extends User {
  is_active: boolean;
}

interface AnalyticsFiltersProps {
  matterGuid: string | null;
  userGuid: string | null;
  matters: AnalyticsMatter[];
  users: AnalyticsFilterUser[];
  onMatterChange: (guid: string | null) => void;
  onUserChange: (guid: string | null) => void;
}

export const AnalyticsFiltersBar = ({
  matterGuid,
  userGuid,
  matters,
  users,
  onMatterChange,
  onUserChange,
}: AnalyticsFiltersProps) => {
  const [matterComboboxOpen, setMatterComboboxOpen] = useState(false);
  const [userComboboxOpen, setUserComboboxOpen] = useState(false);

  const selectedMatterLabel = matterGuid
    ? matters?.find(m => m.guid === matterGuid)?.name || 'Select matter...'
    : 'All Matters';
  const selectedUserLabel = userGuid
    ? fullName(users?.find(u => u.guid === userGuid)) || 'Select user...'
    : 'All Users';

  return (
    <div className="flex flex-wrap items-center gap-4">
      {/* Matter Filter */}
      <Popover open={matterComboboxOpen} onOpenChange={setMatterComboboxOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={matterComboboxOpen}
            className={cn(
              "w-[200px] justify-between transition-all duration-200",
              matterGuid && "border-crimson-300 bg-crimson-50/30 hover:bg-crimson-50/50"
            )}
          >
            <span className="truncate" title={selectedMatterLabel}>{selectedMatterLabel}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0">
          <Command>
            <CommandInput placeholder="Search matters..." />
            <CommandList>
              <CommandEmpty>No matter found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="all-matters"
                  onSelect={() => {
                    onMatterChange(null);
                    setMatterComboboxOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", !matterGuid ? "opacity-100" : "opacity-0")} />
                  All Matters
                </CommandItem>
                {matters?.map((matter) => (
                  <CommandItem
                    key={matter.guid}
                    value={matter.name}
                    onSelect={() => {
                      onMatterChange(matter.guid);
                      setMatterComboboxOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", matterGuid === matter.guid ? "opacity-100" : "opacity-0")} />
                    {matter.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* User Filter */}
      <Popover open={userComboboxOpen} onOpenChange={setUserComboboxOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={userComboboxOpen}
            className={cn(
              "w-[200px] justify-between transition-all duration-200",
              userGuid && "border-crimson-300 bg-crimson-50/30 hover:bg-crimson-50/50"
            )}
          >
            <span className="truncate" title={selectedUserLabel}>{selectedUserLabel}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0">
          <Command>
            <CommandInput placeholder="Search users..." />
            <CommandList>
              <CommandEmpty>No user found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="all-users"
                  onSelect={() => {
                    onUserChange(null);
                    setUserComboboxOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", !userGuid ? "opacity-100" : "opacity-0")} />
                  All Users
                </CommandItem>
                {users?.map((user) => (
                  <CommandItem
                    key={user.guid}
                    value={`${fullName(user)}-${user.guid}`}
                    onSelect={() => {
                      onUserChange(user.guid);
                      setUserComboboxOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", userGuid === user.guid ? "opacity-100" : "opacity-0")} />
                    <span>{fullName(user)}</span>
                    {!user.is_active && (
                      <Badge
                        variant="outline"
                        className="ml-2 border-amber-200 bg-amber-50 px-1.5 py-0 text-[10px] font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
                      >
                        Account inactive
                      </Badge>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Clear Filters */}
      {(matterGuid || userGuid) && (
        <Button
          variant="ghost"
          size="sm"
          text="Clear filters"
          onClick={() => {
            onMatterChange(null);
            onUserChange(null);
          }}
          className="text-crimson-600 transition-all hover:bg-crimson-50 hover:text-crimson-700"
        />
      )}
    </div>
  );
};
